import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, Space, message, Typography } from "@mimber/ui-web"
import { Form, InputField, SwitchField } from "@mimber/forms"
import { useUpdateOrganizationProfile } from "@mimber/client/mimber-organization"
import type { OrganizationPublicSettingsFormProps } from "./OrganizationPublicSettingsForm.types"
import {
  organizationPublicSettingsSchema,
  type OrganizationPublicSettingsFormData,
} from "./OrganizationPublicSettingsForm.schema"

const { Paragraph } = Typography

export const OrganizationPublicSettingsForm: React.FunctionComponent<
  OrganizationPublicSettingsFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)

  const form = useForm<OrganizationPublicSettingsFormData>({
    defaultValues: {
      slug: organization.slug || "",
      isPublic: organization.isPublic || false,
      publicDescription: organization.publicDescription || "",
      publicContactEmail: organization.publicContactEmail || "",
    },
    resolver: zodResolver(organizationPublicSettingsSchema),
    mode: "onBlur",
  })

  const {
    watch,
    formState: { isDirty },
    reset,
  } = form

  const slug = watch("slug")
  const mimberAppUrl =
    import.meta.env.VITE_MIMBER_APP_URL || "app-dev.mimber.com"
  const publicUrlHost = new URL(
    /^https?:\/\//.test(mimberAppUrl) ? mimberAppUrl : `https://${mimberAppUrl}`
  ).host

  const onSubmit = (data: OrganizationPublicSettingsFormData) => {
    const payload = {
      slug: data.slug,
      isPublic: data.isPublic,
      publicDescription: data.publicDescription || null,
      publicContactEmail: data.publicContactEmail || null,
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        message.success("Public profile settings updated successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(
          error.message || "Failed to update public profile settings"
        )
      },
    })
  }

  const isLoading = updateProfile.isPending

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={disabled || isLoading}
      layout="vertical"
    >
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Control the visibility and information displayed on your public profile
      </Paragraph>

      <InputField
        name="slug"
        label="Organization Slug"
        placeholder="your-organization"
        help={slug ? `Available at: ${slug}.${publicUrlHost}` : undefined}
        required
      />

      <SwitchField
        name="isPublic"
        label="Make Profile Public"
        description="When enabled, your profile will be visible to anyone with the link"
      />

      <InputField
        name="publicDescription"
        label="Public Description"
        placeholder="Brief description for public profile"
        multiline
        rows={3}
      />

      <InputField
        name="publicContactEmail"
        label="Public Contact Email"
        placeholder="contact@your-organization.com"
        type="email"
      />

      <Space style={{ marginTop: 24 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={disabled || !isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>

        <Button
          onClick={() => reset()}
          disabled={disabled || !isDirty || isLoading}
        >
          Reset
        </Button>
      </Space>
    </Form>
  )
}
